import { Input } from "@/shadcn/components/ui/input";
import { Textarea } from "@/shadcn/components/ui/textarea";
import {
  useAuth,
  useFeedbackTicketDesktop,
  useI18N,
  useModalHeight,
} from "common/hooks";
import { useEffect, useRef, useState } from "react";
import { readFile } from "@/helpers/cropImage";
import Zoom from "react-medium-image-zoom";
import { AddPhotoImageData } from "common/types";
import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import { useGetInTouchModal } from "@/hooks/useGetInTouchModal";
import { useCustomToast } from "@/hooks/useCustomToast";
import WideButton from "@/components/common/WideButton";
import { Storage } from "common/storage";
import AnalyticsService from "@/services/analytics";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";
import { ModalHeader } from "@/components/common/ModalHeader";

export function GetInTouchModal() {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const [notes, setNotes] = useState("");
  const { user } = useAuth();
  const showToast = useCustomToast();
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const { modalStyle, modalClassName } = useModalHeight();
  const [errors, setErrors] = useState({
    note: false,
  });
  const noteRef = useRef(null);

  const { isGetInTouchModalVisible, hideGetInTouchModal } =
    useGetInTouchModal();

  const { mutate, error, isPending, data } = useFeedbackTicketDesktop();

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler("appFeedbackErrorSubmit");
      AnalyticsService.trackMx("appFeedbackErrorSubmit", {});

      showToast(error?.message, "error");
    }
  }, [isDark, error]);

  useEffect(() => {
    if (data && data.data.feedback === "saved") {
      showToast(data?.message, "success");
      setAttachedImages([]);
      setNotes("");
      setErrors({
        note: false,
      });
      hideGetInTouchModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isDark]);

  const validateFields = () => {
    const newErrors = {
      note: notes.length < 5,
    };

    setErrors(newErrors);

    const isNoteValid = !newErrors.note;

    if (!isNoteValid && noteRef.current) {
      noteRef.current.scrollIntoView({ behavior: "smooth" });
      // Focus the textarea after scrolling for keyboard users
      setTimeout(() => {
        if (noteRef.current) {
          noteRef.current.focus();
        }
      }, 100);
    }

    return isNoteValid;
  };

  const submit = async () => {
    if (!validateFields()) {
      return;
    }
    // console.log("inside submit");
    if (user) {
      AnalyticsService.trackGAEventHandler("appFeedbackSubmit");
      AnalyticsService.trackMx("appFeedbackSubmit", {});

      mutate({
        accessToken: Storage.getCurrentUserToken(),
        notes,
        images: attachedImages,
      });
    } else {
      showToast(i18n.get("mustBeLoggedInToSubmitReview"), "error");
    }
  };

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the modal opens
  useEffect(() => {
    if (isGetInTouchModalVisible && firstFocusRef.current) {
      setTimeout(() => {
        firstFocusRef.current?.focus();
      }, 100);
    }
  }, [isGetInTouchModalVisible]);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isGetInTouchModalVisible}
      onClose={() => hideGetInTouchModal()}
      classNames={{
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
    >
      {isPending && <Loader />}
      <section
        style={modalStyle}
        className={cn(modalClassName)}
        aria-labelledby="modal-title"
      >
        <ModalHeader
          title={i18n.get("getInTouch")}
          titleId="modal-title"
          onClose={() => {
            setAttachedImages([]);
            setNotes("");
            hideGetInTouchModal();
          }}
          firstFocusRef={firstFocusRef}
        />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex flex-col flex-grow"
        >
          <div
            className="flex-grow overflow-y-auto py-3 px-3"
          >
            <fieldset disabled={isPending} className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex border-0 p-0 m-0 min-w-0">
              <legend className="sr-only">Feedback form</legend>
              <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
                <div className="self-stretch text-fg-neutral-strong body-l-semibold md:title-s-semibold">
                  {i18n.get("letUsKnowYourFeedback")}
                </div>
              </div>

              <div className="w-full flex-col justify-center items-start gap-1 flex">
                <div className="w-full">
                  <label htmlFor="review" className="sr-only">
                    {i18n.get("getInTouchPlaceholderText")}
                  </label>
                  <Textarea
                    id="review"
                    placeholder={i18n.get("getInTouchPlaceholderText")}
                    className="scrollbar-thin h-28 mt-5"
                    value={notes}
                    aria-describedby={errors.note ? "charCount reviewError" : "charCount"}
                    aria-invalid={errors.note ? "true" : "false"}
                    onChange={(e) => {
                      setNotes(e.target.value);
                    }}
                    maxLength={500}
                    ref={noteRef}
                  />
                  <div
                    id="charCount"
                    className="label-s-medium text-fg-neutral-subtle mt-3"
                  >
                    <span className={notes.length < 5 ? "text-red-600 font-semibold" : ""}>
                      <span aria-hidden="true">{notes.length < 5 && "⚠️ "}</span>
                      {notes.length < 5 && <span className="sr-only">Warning: </span>}
                      {notes.length}
                    </span>
                    /500 Characters - at least 5 characters required
                  </div>
                  {errors.note && (
                    <div id="reviewError" className="text-red-600 font-semibold mt-1" role="alert">
                      <span aria-hidden="true">⚠️ </span>
                      <span className="sr-only">Error: </span>
                      {i18n.get("getInTouchPlaceholderText")}
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full flex-col justify-center items-start gap-1 flex">
                <div className="grid gap-4 w-full">
                  {attachedImages.map((image, index) => (
                    <div key={index} className="grid gap-2">
                      <div className="relative w-full h-auto">
                        <Zoom>
                          <img
                            src={image.imageUrl}
                            alt={`Attached image ${index + 1}`}
                            className="w-full h-[300px] object-cover rounded-[10px]"
                          />
                        </Zoom>
                        <StandardButton
                          onClick={() => {
                            setAttachedImages(
                              attachedImages.filter(
                                (_, imgIndex) => imgIndex !== index,
                              ),
                            );
                          }}
                          variant="removeImage"
                          position="absolute"
                          className="top-2 right-3 m-2"
                          imageSrc="/images/Close.svg"
                          imageAlt="Remove image"
                          imageContainer={true}
                          imageContainerClassName="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex"
                          aria-label={`Remove attached image ${index + 1}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </fieldset>
          </div>

          <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-border-token-brand-minimal sticky bottom-0 bg-bg-brand-secondary-default z-1000">
            <div className="flex-col justify-end items-center gap-4 flex md:w-full">
              <div className="w-full justify-start items-center gap-4 inline-flex">
                <label
                  htmlFor="picture"
                  className="grow shrink basis-0 h-12 px-6 py-3 bg-container-brand-minimal rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer"
                  aria-label="Add photos (multiple files allowed)"
                >
                  <div className="text-center text-fg-neutral-strong label-l-bold w-full">
                    {i18n.get("addPhotos")}
                  </div>
                  <Input
                    id="picture"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onFileChange}
                    className="sr-only"
                  />
                </label>
              </div>

              <div className="w-full justify-center items-center inline-flex">
                <WideButton
                  className="grow"
                  disabled={notes.length < 5}
                  buttonType="submit"
                  aria-label="Submit feedback"
                >
                  {i18n.get("submit")}
                </WideButton>
              </div>
            </div>
          </footer>
        </form>
      </section>
    </Modal>
  );
}
